import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Alliance } from '../types/alliance';
import { useProfileStore } from './profileStore';
import { useNotificationStore } from './notificationStore';
import { useCreditStore } from './creditStore';

interface AllianceState {
  alliances: Alliance[];
  loading: boolean;
  error: string | null;
  fetchAlliances: () => Promise<void>;
  deleteAlliance: (
    allianceId: string
  ) => Promise<{ success: boolean; error?: undefined } | { error: string; success?: undefined }>;
  requestAlliance: (
    toUserId: string,
    message: string
  ) => Promise<
    | { success: boolean; id?: string; error?: undefined }
    | { error: string; success?: undefined; id?: undefined }
  >;
  acceptAllianceRequest: (
    allianceId: string
  ) => Promise<{ success: boolean; error?: undefined } | { error: string; success?: undefined }>;
  retireAlliance: (
    allianceId: string
  ) => Promise<{ success: boolean; error?: undefined } | { error: string; success?: undefined }>;
  declineAllianceRequest: (
    allianceId: string,
    rejectReason?: string
  ) => Promise<{ success: boolean; error?: undefined } | { error: string; success?: undefined }>;
  setHighlightedServiceCategory: (
    allianceId: string,
    isUser1Current: boolean,
    serviceCategoryId: number
  ) => Promise<{ success: boolean; error?: undefined } | { error: string; success?: undefined }>;

  getAcceptedAllianceRequestCount: (userId: string) => Promise<number>;
}

export const useAllianceStore = create<AllianceState>((set, get) => ({
  alliances: [],
  loading: false,
  error: null,
  fetchAlliances: async () => {
    if (get().loading) return;

    const { profile } = useProfileStore.getState();

    if (!profile) throw new Error('Profile not found');
    set({ loading: true });
    try {
      const { data, error } = await supabase.rpc('get_profile_alliances');

      if (error) throw error;
      const alliances = data as unknown as Alliance[];

      set({ alliances, error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  deleteAlliance: async (allianceId: string) => {
    try {
      const { error } = await supabase.from('alliances').delete().eq('id', allianceId);

      if (error) throw error;

      get().fetchAlliances();
      return { success: true };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  requestAlliance: async (toUserId: string, message: string) => {
    try {
      const { data, error } = await supabase.rpc('request_alliance', {
        p_other_user_id: toUserId,
        p_request_message: message,
      });

      if (error) throw error;
      // Refresh credit balance after request (in case credits were consumed)
      useCreditStore.getState().refreshBalance();
      get().fetchAlliances();
      return { success: true, id: data };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  acceptAllianceRequest: async (allianceId: string) => {
    try {
      const { error } = await supabase
        .from('alliances')
        .update({ status: 'accepted' })
        .eq('id', allianceId)
        .eq('status', 'pending');

      if (error) throw error;
      useNotificationStore.getState().fetchUnreadCount();
      get().fetchAlliances();
      return { success: true };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  retireAlliance: async (allianceId: string) => {
    try {
      const { error } = await supabase
        .from('alliances')
        .update({ status: 'retired' })
        .eq('id', allianceId)
        .eq('status', 'accepted');

      if (error) throw error;

      get().fetchAlliances();
      return { success: true };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  declineAllianceRequest: async (allianceId: string, rejectReason?: string) => {
    try {
      const { error } = await supabase
        .from('alliances')
        .update({
          status: 'rejected',
          rejected_reason: rejectReason || null,
        })
        .eq('id', allianceId)
        .eq('status', 'pending');

      if (error) throw error;
      useNotificationStore.getState().fetchUnreadCount();
      get().fetchAlliances();
      return { success: true };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  setHighlightedServiceCategory: async (
    allianceId: string,
    isUser1Current: boolean,
    serviceCategoryId: number
  ) => {
    try {
      const updateField = isUser1Current
        ? 'user_2_highlighted_service_category_id'
        : 'user_1_highlighted_service_category_id';

      const { error } = await supabase
        .from('alliances')
        .update({ [updateField]: serviceCategoryId })
        .eq('id', allianceId);

      if (error) throw error;

      get().fetchAlliances();
      return { success: true };
    } catch (error) {
      return { error: (error as Error).message };
    }
  },

  getAcceptedAllianceRequestCount: async (userId: string) => {
    try {
      const { data: count, error } = await supabase.rpc('get_accepted_alliance_count', {
        user_uuid: userId,
      });

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error fetching alliance request count:', (error as Error).message);
      return 0;
    }
  },
}));
